import * as React from "react";
import ReactGridLayout, { EventCallback, Layout, LayoutItem, useContainerWidth, verticalCompactor } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { DashboardLayoutItem } from "../../types";
import { GRID_COLUMNS, GRID_MARGIN, GRID_ROW_HEIGHT } from "./layout/layoutUtils";
import { WIDGET_REGISTRY, WidgetProps, WidgetType } from "./widgetRegistry";
import DashboardWidget from "./DashboardWidget";
import useDragAutoScroll from "../../hooks/use-drag-auto-scroll";

export const DRAG_HANDLE_SELECTOR = ".dashboard-widget-drag-handle";

interface IProps {
  items: DashboardLayoutItem[];
  editing: boolean;
  onChange(items: DashboardLayoutItem[]): void;
  onRemove(i: string): void;
  widgetProps: WidgetProps;
}

const GRID_CONFIG = {
  cols: GRID_COLUMNS,
  rowHeight: GRID_ROW_HEIGHT,
  // No vertical margin: each item reserves GRID_MARGIN at its bottom instead, so heights move in GRID_ROW_HEIGHT steps
  margin: [GRID_MARGIN, 0] as [number, number],
  containerPadding: [0, 0] as [number, number],
};

const DashboardGrid = ({ items, editing, onChange, onRemove, widgetProps }: IProps) => {
  const { width, containerRef, mounted } = useContainerWidth();
  const autoScroll = useDragAutoScroll();

  const onInteraction = React.useCallback<EventCallback>(
    (_layout, _oldItem, _newItem, _placeholder, event, element) => autoScroll.track(event, element),
    [autoScroll.track]
  );

  const layout = React.useMemo<LayoutItem[]>(
    () =>
      items.map(({ i, x, y, w, h, type }) => {
        const minSize = WIDGET_REGISTRY[type as WidgetType]?.minSize;
        return { i, x, y, w, h, minW: minSize?.minW, minH: minSize?.minH };
      }),
    [items]
  );

  const onLayoutChange = React.useCallback(
    (nextLayout: Layout) => {
      const byId = new Map(nextLayout.map((l) => [l.i, l]));
      let changed = false;
      const nextItems = items.map((item) => {
        const l = byId.get(item.i);
        if (!l) return item;
        if (l.x === item.x && l.y === item.y && l.w === item.w && l.h === item.h) return item;
        changed = true;
        return { ...item, x: l.x, y: l.y, w: l.w, h: l.h };
      });
      if (changed) onChange(nextItems);
    },
    [items, onChange]
  );

  // Each item's bottom 24px (pb-6) is the vertical gap, so the corner resize handle is lifted onto the card's corner
  return (
    <div ref={containerRef} className="w-full [&_.react-resizable-handle-se]:bottom-6!">
      {mounted && (
        <ReactGridLayout
          width={width}
          layout={layout}
          gridConfig={GRID_CONFIG}
          dragConfig={{ enabled: editing, handle: DRAG_HANDLE_SELECTOR }}
          resizeConfig={{ enabled: editing }}
          compactor={verticalCompactor}
          onLayoutChange={onLayoutChange}
          onDragStart={onInteraction}
          onDrag={onInteraction}
          onDragStop={autoScroll.stop}
          onResizeStart={onInteraction}
          onResize={onInteraction}
          onResizeStop={autoScroll.stop}
        >
          {items.map((item) => (
            <div key={item.i}>
              <div className="h-full pb-6">
                <DashboardWidget
                  item={item}
                  editing={editing}
                  onRemove={() => onRemove(item.i)}
                  widgetProps={widgetProps}
                />
              </div>
            </div>
          ))}
        </ReactGridLayout>
      )}
    </div>
  );
};

export default DashboardGrid;
